"use client";

import * as React from "react";

/**
 * Tracks whether a horizontal scroller is at its start / end.
 *
 * Several carousels drive their arrows with a blind `el.scrollBy(...)` and keep
 * no scroll-position state, so both arrows stay live at both ends and clicking
 * one at the boundary does nothing. QA: "當這個元件到達最右端時，這個按鈕應該
 * 變灰。 另外，左邊的按鈕無法點擊".
 *
 * Returns a ref to attach to the scroll container plus the two edge flags.
 * Recomputes on scroll, on resize, and when the content size changes.
 */
export function useScrollEdges<T extends HTMLElement = HTMLDivElement>() {
  const ref = React.useRef<T>(null);
  const [atStart, setAtStart] = React.useState(true);
  const [atEnd, setAtEnd] = React.useState(false);

  const measure = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;

    // Tolerance has to account for the scroller's own horizontal padding, not
    // just sub-pixel rounding. These ribbons use the `-mx-4 px-4 sm:px-6 lg:px-8`
    // bleed pattern with `snap-mandatory`, so the browser snaps to the first
    // card and the resting scrollLeft is the padding value (32px at lg), never
    // 0. A naive `scrollLeft <= 1` therefore reports "not at start" on a
    // freshly-loaded carousel and the prev arrow never disables.
    const cs = getComputedStyle(el);
    const padLeft = parseFloat(cs.paddingLeft) || 0;
    const padRight = parseFloat(cs.paddingRight) || 0;
    const EPSILON = 2;

    const max = el.scrollWidth - el.clientWidth;
    setAtStart(el.scrollLeft <= padLeft + EPSILON);
    setAtEnd(max <= EPSILON || el.scrollLeft >= max - padRight - EPSILON);
  }, []);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);

    // Content can arrive after mount (images, lazy data), which changes
    // scrollWidth without firing scroll or resize.
    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    for (const child of Array.from(el.children)) ro?.observe(child);

    return () => {
      el.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
      ro?.disconnect();
    };
  }, [measure]);

  return { ref, atStart, atEnd, measure };
}
